import type { Household } from 'src/types/household';
import React, { useState } from 'react';
import { Button as ButtonV2 } from '@savvy-kit/components/button';
import { ButtonGroup } from '@savvy-kit/components/button-group';
import { DialogFooter, DialogHeader, DialogTitle } from '@savvy-kit/components/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@savvy-kit/components/hover-card';
import InputField from '@savvy-kit/patterns/input-field';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { TrackedButton as TrackedButtonV2, TrackedIconButton } from 'src/components/tracked';
import TrackedButton from 'src/components/tracked/tracked-button';
import TrackedDialog from 'src/components/tracked/tracked-dialog';
import { ORION_URL } from 'src/constants/logo-urls/financial-institution-logo';
import { graphql } from 'src/gql';
import { handleMutationError } from 'src/utils/handle-mutation-error';
import { providerImgSrc } from 'src/utils/image';

import { useMutationWithErrorLogging } from '@savvy/graphql-hooks/useMutationWithErrorLogging';

const PROCESS_ONBOARDED_CLIENT_MUTATION = graphql(/* GraphQL */ `
  mutation ProcessOnboardedClient($input: ProcessOnboardedClientInput!) {
    processOnboardedClient(input: $input) {
      errorMessage
      success
    }
  }
`);

type ConnectClientToOrionButtonProps = {
  readonly household: Household;
};

enum OrionConnectMethod {
  Auto = 'Auto',
  Manual = 'Manual',
}
const OrionConnectMethodLabels: Record<OrionConnectMethod, string> = {
  [OrionConnectMethod.Auto]: 'Auto Connect to Orion',
  [OrionConnectMethod.Manual]: 'Manual Connect to Orion',
};

function ConnectClientToOrionButton({ household }: ConnectClientToOrionButtonProps) {
  const [selectedConnectMethod, setSelectedConnectMethod] = useState(OrionConnectMethod.Auto);
  const [processOnboardedClient] = useMutationWithErrorLogging(PROCESS_ONBOARDED_CLIENT_MUTATION);
  const [manualConnectionDialogOpen, setManualConnectionDialogOpen] = useState(false);
  const [orionHouseholdId, setOrionHouseholdId] = useState('');

  const handleMenuItemClick = (connectMethod: OrionConnectMethod) => {
    setSelectedConnectMethod(connectMethod);
  };

  const handleButtonClick = (connectMethod: OrionConnectMethod) => {
    if (connectMethod === OrionConnectMethod.Auto) {
      connectToOrion();
    } else {
      setManualConnectionDialogOpen(true);
    }
  };

  const connectToOrion = async (additionalParams = {}) => {
    const toastId = toast.loading('Connecting...');
    try {
      const { data } = await processOnboardedClient({
        variables: {
          input: {
            userId: household.primaryClient?.user.id,
            ...additionalParams,
          },
        },
      });

      if (data?.processOnboardedClient?.success) {
        toast.success('Success! The new accounts should show up in a few moments.', {
          id: toastId,
          style: { paddingLeft: '18px' },
          duration: 6000,
        });
      } else {
        toast.error(data?.processOnboardedClient?.errorMessage || 'An error occurred', { id: toastId });
      }
    } catch (error) {
      handleMutationError(error, 'An unexpected error occurred', { id: toastId });
    }
  };

  return (
    <>
      <ButtonGroup className="w-full">
        <TrackedButtonV2
          variant="secondary"
          elementName="connect-client-to-orion-button"
          className="w-[210px]"
          onClick={() => {
            handleButtonClick(selectedConnectMethod);
          }}
          icon={
            // eslint-disable-next-line @next/next/no-img-element -- providerImgSrc returns a tiny provider icon URL for button chrome
            <img src={providerImgSrc(ORION_URL)} alt="Orion" className="size-4 bg-inherit" />
          }
          iconPosition="right"
        >
          {OrionConnectMethodLabels[selectedConnectMethod]}
        </TrackedButtonV2>
        <HoverCard>
          <HoverCardTrigger
            render={<TrackedIconButton elementName="connect-client-to-orion-button-dropdown" variant="secondary" />}
          >
            <ChevronDown />
          </HoverCardTrigger>
          <HoverCardContent className="flex flex-col gap-2 w-[140px] px-3 py-2" align="end">
            {Object.values(OrionConnectMethod).map((method) => (
              <ButtonV2
                key={method}
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => handleMenuItemClick(method)}
              >
                {`${method} Connect`}
              </ButtonV2>
            ))}
          </HoverCardContent>
        </HoverCard>
      </ButtonGroup>

      <TrackedDialog
        elementName="connect-client-to-orion-button-manual-connection-dialog"
        open={manualConnectionDialogOpen}
        onOpenChange={(nextOpen) => !nextOpen && setManualConnectionDialogOpen(false)}
        className="sm:max-w-[400px]"
      >
        <DialogHeader>
          <DialogTitle>Specify the Orion Household ID for this client</DialogTitle>
        </DialogHeader>
        <InputField
          id="connect-client-orion-household-id"
          autoFocus
          label="Orion Household ID"
          value={orionHouseholdId}
          onChange={(e) => setOrionHouseholdId(e.target.value)}
        />
        <DialogFooter>
          <TrackedButton
            elementName="connect-client-to-orion-button-cancel-button"
            onClick={() => {
              setManualConnectionDialogOpen(false);
            }}
          >
            Cancel
          </TrackedButton>
          <TrackedButton
            elementName="connect-client-to-orion-button-submit-button"
            variant="default"
            onClick={() => {
              connectToOrion({ orionHouseholdId });
              setManualConnectionDialogOpen(false);
            }}
          >
            Submit
          </TrackedButton>
        </DialogFooter>
      </TrackedDialog>
    </>
  );
}

export default ConnectClientToOrionButton;
